import { uiText } from '../i18n/uiText';
import {
  LayoutDashboard,
  ClipboardList,
  CalendarDays,
  CheckSquare,
  Building2,
  FileText,
  Users,
  ShieldCheck,
  FolderKanban,
  Bell,
  Search,
  AlertTriangle,
  Mail,
  TrendingUp,
  ClipboardCheck,
  Sliders,
  Bot,
  History,
  ServerCog,
} from 'lucide-react';
import { hasRole, isAuditeeRole, rolesForResource } from '../utils/permissions';

const ADMIN_ROLES = rolesForResource('admin');
const AUDIT_ROLES = rolesForResource('audits');
const DASHBOARD_ROLES = rolesForResource('dashboard');
const SELF_CHECK_ROLES = rolesForResource('self_check');
const COMPANY_SUMMARY_ROLES = rolesForResource('executive_dashboard');
const HOME_ROLES = Array.from(new Set([...DASHBOARD_ROLES, ...COMPANY_SUMMARY_ROLES]));
const MANAGEMENT_ROLES = rolesForResource('management');
const AUDITOR_POOL_ROLES = rolesForResource('auditor_pool');
const LOCAL_AI_ROLES = ['admin', 'auditor'];

export const APP_MODULES = [
  // Представитель управляет только собственной организацией и её анкетой.
  {
    key: 'organization',
    label: uiText("Моя организация"),
    to: '/my-organization',
    icon: Building2,
    roles: SELF_CHECK_ROLES,
    description: uiText("Сотрудники организации, приглашения и анкета благонадёжности."),
    children: [
      { to: '/my-organization', label: uiText("Моя организация"), icon: Building2, roles: SELF_CHECK_ROLES },
      { to: '/self-check', label: uiText("Анкета организации"), icon: ClipboardCheck, roles: SELF_CHECK_ROLES },
      ...(process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' ? [{ to: '/my-qualification', label: uiText("Квалификация организации"), icon: ShieldCheck, roles: SELF_CHECK_ROLES }] : []),
    ],
  },
  {
    key: 'dashboard',
    label: uiText("Главная"),
    to: '/dashboard',
    icon: LayoutDashboard,
    roles: HOME_ROLES,
    useFirstVisibleChildAsEntry: true,
    description: uiText("Единая точка входа: внимание, активные процессы, календарь и последние события."),
    children: [
      { to: '/dashboard', label: uiText("Обзор"), icon: LayoutDashboard, roles: DASHBOARD_ROLES },
      { to: '/company-summary', label: uiText("Сводка по компаниям"), icon: TrendingUp, roles: COMPANY_SUMMARY_ROLES },
    ],
  },
  {
    key: 'audits',
    label: uiText("Аудиты"),
    to: '/audits',
    icon: ClipboardList,
    roles: AUDIT_ROLES,
    description: uiText("Единый список аудитов всех типов. Фильтр по типу контрагента — на странице."),
    children: [
      { to: '/audits', label: uiText("Список аудитов"), icon: ClipboardList, roles: AUDIT_ROLES },
      { to: '/audit-calendar', label: uiText("Календарь"), icon: CalendarDays, roles: AUDIT_ROLES },
      { to: '/assignment-notifications', label: uiText("Мои задания"), icon: Bell, roles: MANAGEMENT_ROLES },
    ],
  },
  {
    key: 'assistant',
    label: uiText("ИИ-ассистент"),
    to: '/ai-assistant',
    icon: Bot,
    description: uiText("Работа с документами и вопросами к ИИ-ассистенту."),
    roles: LOCAL_AI_ROLES,
    children: [
      { to: '/ai-assistant', label: uiText("ИИ-ассистент"), icon: Bot, roles: LOCAL_AI_ROLES },
    ],
  },
  {
    key: 'management',
    label: uiText("Управление"),
    to: '/management',
    icon: ShieldCheck,
    description: uiText("Документы, справочники и настройки для подготовки и проведения аудитов."),
    roles: MANAGEMENT_ROLES,
    children: [
      { to: '/management', label: uiText("Обзор управления"), icon: ShieldCheck, navPriority: 1, group: uiText("Основное") },
      { to: '/companies', label: uiText("Компании"), icon: Building2, roles: MANAGEMENT_ROLES, navPriority: 2, group: uiText("Компании и люди") },
      ...(process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' ? [
        { to: '/supplier-needs', label: uiText("Потребности и исполнители"), icon: Search, roles: MANAGEMENT_ROLES, navPriority: 3, group: uiText("Основное") },
        { to: process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' ? '/requirements' : '/supplier-settings', label: process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' ? 'Библиотека требований' : uiText("Требования и правила оценки"), icon: ShieldCheck, roles: MANAGEMENT_ROLES, group: uiText("Настройки") },
      ] : []),
      ...(process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' ? [{ to: '/qualification', label: uiText("Квалификационная оценка"), icon: ShieldCheck, roles: MANAGEMENT_ROLES, navPriority: 2, group: uiText("Основное") }] : []),
      { to: '/users', label: uiText("Учётные записи"), icon: Users, roles: ADMIN_ROLES, navPriority: 3, group: uiText("Компании и люди") },
      { to: '/auditor-pool', label: uiText("Аудиторы"), icon: Users, roles: AUDITOR_POOL_ROLES, navPriority: 4, group: uiText("Компании и люди") },
      { to: '/templates', label: uiText("Шаблоны чек-листов"), icon: CheckSquare, roles: MANAGEMENT_ROLES, navPriority: 5, group: uiText("Подготовка аудита") },
      { to: '/contracts', label: uiText("Договоры"), icon: FileText, roles: MANAGEMENT_ROLES, group: uiText("Подготовка аудита") },
      { to: '/audit-plans', label: uiText("Годовые планы"), icon: CalendarDays, roles: ADMIN_ROLES, group: uiText("Подготовка аудита") },
      { to: '/audit-programs', label: uiText("Планы аудитов"), icon: ClipboardList, roles: MANAGEMENT_ROLES, group: uiText("Проведение и документы") },
      { to: '/audit-reports', label: uiText("Отчёты по аудитам"), icon: FileText, roles: MANAGEMENT_ROLES, group: uiText("Проведение и документы") },
      { to: '/corrective-plans', label: uiText("ПКПД"), icon: CheckSquare, roles: MANAGEMENT_ROLES, group: uiText("Проведение и документы") },
      { to: '/outgoing-emails', label: uiText("Сообщения"), icon: Mail, roles: ADMIN_ROLES, group: uiText("Системные операции") },
      { to: '/admin/audit-trail', label: uiText("Журнал и корзина"), icon: History, roles: ADMIN_ROLES, group: uiText("Системные операции") },
      { to: '/admin/system-control', label: uiText("Контроль системы"), icon: ServerCog, roles: ADMIN_ROLES, group: uiText("Системные операции") },
      { to: '/admin/self-check-weights', label: uiText("Благонадёжность контрагента"), icon: Sliders, roles: ADMIN_ROLES, group: uiText("Процесс и контроль") },
    ],
  },
];

export const UTILITY_LINKS = [
  { to: '/issues', label: uiText("Замечания"), icon: AlertTriangle, roles: AUDIT_ROLES, moduleKey: 'audits' },
  { to: '/corrective-actions', label: uiText("Корректирующие действия"), icon: CheckSquare, roles: AUDIT_ROLES, moduleKey: 'audits' },
  { to: '/corrective-plans', label: uiText("ПКПД"), icon: ClipboardCheck, roles: AUDIT_ROLES, moduleKey: 'audits' },
  { to: '/search', label: uiText("Поиск"), icon: Search, roles: AUDIT_ROLES, moduleKey: 'audits' },
];

const DYNAMIC_ROUTE_PATTERNS = [
  { pattern: /^\/audit-preparation$/, label: () => 'Подготовить аудит' },
  { pattern: /^\/classifier$/, label: () => 'Редактор требований' },
  { pattern: /^\/supplier-settings$/, label: () => 'Правила оценки' },
  { pattern: /^\/audits\/(\d+)$/, label: (m) => uiText('Аудит #{id}', { id: m[1] }) },
  { pattern: /^\/ai-assistant$/, label: () => uiText("ИИ-ассистент") },
  { pattern: /^\/notifications$/, label: () => uiText("Уведомления") },
  { pattern: /^\/search$/, label: () => uiText("Глобальный поиск") },
  { pattern: /^\/issues$/, label: () => uiText("Замечания") },
  { pattern: /^\/corrective-actions$/, label: () => uiText("Корректирующие действия") },
  { pattern: /^\/security$/, label: () => uiText("Пароль и доступ") },
  { pattern: /^\/self-check\/review\/(\d+)$/, label: () => uiText("Проверка анкеты организации") },
  { pattern: /^\/my-organization$/, label: () => uiText("Моя организация") },
  { pattern: /^\/audit-plans$/, label: () => uiText("Годовые планы") },
  { pattern: /^\/audit-programs$/, label: () => uiText("Планы аудитов") },
  { pattern: /^\/audit-reports$/, label: () => uiText("Отчёты по аудитам") },
  { pattern: /^\/corrective-plans$/, label: () => uiText("ПКПД") },
  { pattern: /^\/outgoing-emails$/, label: () => uiText("Сообщения") },
  { pattern: /^\/help$/, label: () => uiText("Помощь") },
  { pattern: /^\/admin\/audit-trail$/, label: () => uiText("Журнал и корзина") },
  { pattern: /^\/admin\/system-control$/, label: () => uiText("Контроль системы") },
];

export function itemVisibleForRole(item, role) {
  return hasRole({ role }, item.roles);
}

export function getVisibleModules(role) {
  const primaryOrder = {
    dashboard: 0,
    audits: 1,
    assistant: 2,
    management: 3,
    organization: 4,
  };
  return APP_MODULES.map((module) => {
    const children = (module.children || []).filter((item) => itemVisibleForRole(item, role));
    return {
      ...module,
      to: module.useFirstVisibleChildAsEntry ? (children[0]?.to || module.to) : module.to,
      children,
    };
  })
    .filter((module) => itemVisibleForRole(module, role))
    // Внешнему пользователю нужен рабочий кабинет, а не внутреннее меню AAC.
    // Доступы API не меняются: аналитика остаётся доступна по существующим
    // правилам, но не конкурирует с основными задачами в верхней навигации.
    .filter((module) => !isAuditeeRole(role) || ['dashboard', 'audits', 'organization'].includes(module.key))
    .map((module) => {
      if (!isAuditeeRole(role)) return module;
      if (module.key === 'audits') {
        return {
          ...module,
          label: uiText("Мои аудиты"),
          description: uiText("Назначенные вашей организации проверки, вопросы и документы."),
          children: (module.children || []).filter((item) => item.to === '/audits'),
        };
      }
      if (module.key === 'organization') {
        return {
          ...module,
          description: uiText("Данные вашей организации, коллеги и анкета благонадёжности."),
        };
      }
      return module;
    })
    .sort((left, right) => (primaryOrder[left.key] ?? 99) - (primaryOrder[right.key] ?? 99));
}

function routeMatches(pathname, target) {
  return pathname === target || pathname.startsWith(`${target}/`);
}

function findBestChild(module, pathname) {
  return (module.children || [])
    .filter((item) => routeMatches(pathname, item.to))
    .sort((left, right) => right.to.length - left.to.length)[0];
}

export function getCurrentModule(pathname, role) {
  const modules = getVisibleModules(role);
  if (pathname === '/audit-preparation') return modules.find(m => m.key === 'audits') || modules[0] || null;
  if (['/classifier', '/supplier-settings', '/requirements'].includes(pathname)) return modules.find(m => m.key === 'management') || modules[0] || null;
  if (/^\/suppliers\/\d+\/?$/.test(pathname)) {
    return modules.find(module => module.key === (isAuditeeRole(role) ? 'organization' : 'management')) || modules[0] || null;
  }
  const matchedModule = modules.find((module) => {
    const own = routeMatches(pathname, module.to);
    const child = Boolean(findBestChild(module, pathname));
    return own || child;
  });
  if (matchedModule) return matchedModule;

  const matchedUtility = UTILITY_LINKS.find((item) => (
    itemVisibleForRole(item, role)
    && (pathname === item.to || pathname.startsWith(`${item.to}/`))
  ));
  if (matchedUtility?.moduleKey) {
    const utilityModule = modules.find((module) => module.key === matchedUtility.moduleKey);
    if (utilityModule) return utilityModule;
  }

  return modules[0] || null;
}

export function getVisibleUtilityLinks(pathname, role, moduleKey) {
  if (isAuditeeRole(role)) return [];
  return UTILITY_LINKS.filter((item) => {
    if (!itemVisibleForRole(item, role)) return false;
    const activeUtility = pathname === item.to || pathname.startsWith(`${item.to}/`);
    return activeUtility || item.moduleKey === moduleKey;
  });
}

function resolveRouteLabel(pathname, role) {
  if (/^\/suppliers\/\d+\/?$/.test(pathname)) return uiText('Профиль компании');
  const modules = getVisibleModules(role);
  for (const module of modules) {
    if (pathname === module.to) return module.label;
    const child = (module.children || []).find((item) => pathname === item.to);
    if (child) return child.label;
  }
  const dynamic = DYNAMIC_ROUTE_PATTERNS.find((item) => item.pattern.test(pathname));
  if (dynamic) {
    const match = pathname.match(dynamic.pattern);
    return uiText(dynamic.label(match || []));
  }
  for (const module of modules) {
    const child = findBestChild(module, pathname);
    if (child) return child.label;
  }
  const last = pathname.split('/').filter(Boolean).pop() || uiText('Раздел');
  return last.replace(/-/g, ' ');
}

export function getBreadcrumbs(pathname, role) {
  const modules = getVisibleModules(role);
  const currentModule = getCurrentModule(pathname, role);
  const crumbs = [];
  if (currentModule) {
    crumbs.push({ to: currentModule.to, label: currentModule.label });
    const child = findBestChild(currentModule, pathname);
    if (child && child.to !== currentModule.to) {
      crumbs.push({ to: child.to, label: child.label });
    }
  }
  const resolved = resolveRouteLabel(pathname, role);
  if (!crumbs.length || crumbs[crumbs.length - 1]?.label !== resolved) {
    crumbs.push({ to: pathname, label: resolved });
  }
  return crumbs;
}
